import React from 'react';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-container">
          <h1 className="error-boundary-heading">Something went wrong</h1>
          <p className="error-boundary-description">
            We apologize for the inconvenience. The application encountered an unexpected error.
            Please try refreshing the page.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <details className="error-boundary-details">
              <summary className="error-boundary-details-summary">
                Error Details (Development Only)
              </summary>
              <pre className="error-boundary-error-message">{this.state.error.toString()}</pre>
              <pre className="error-boundary-stack-trace">
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div className="error-boundary-button-container">
            <button
              onClick={() => window.location.reload()}
              className="error-boundary-reload-button"
            >
              Reload Page
            </button>
            <button onClick={this.handleReset} className="error-boundary-try-again-button">
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
